import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { ChevronLeft, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const AddLead = () => {
  const navigate = useNavigate();
  const { role, user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    source: 'Direct',
    status: 'New',
    leadType: '',
    visitDate: '',
    visitTime: '',
    assignedTelecaller: '',
    assignedExecutive: '',
    customFields: {}
  });

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  
  // Dynamic Lists
  const [leadSources, setLeadSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [customFieldsDef, setCustomFieldsDef] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, sourcesRes, statusesRes, typesRes, customRes] = await Promise.all([
          api('/users'),
          api('/lead-sources'),
          api('/lead-statuses'),
          api('/lead-types'),
          api('/custom-fields')
        ]);

        if (usersRes.ok) setEmployees(await usersRes.json());
        
        if (sourcesRes.ok) {
          const data = await sourcesRes.json();
          const names = data.map(s => s.name);
          setLeadSources(names);
          if (names.length > 0 && !names.includes(formData.source)) setFormData(p => ({ ...p, source: names[0] }));
        }

        if (statusesRes.ok) {
          const data = await statusesRes.json();
          const names = data.map(s => s.name);
          setLeadStatuses(names);
          if (names.length > 0 && !names.includes(formData.status)) setFormData(p => ({ ...p, status: names[0] }));
        }

        if (typesRes.ok) {
          const data = await typesRes.json();
          setLeadTypes(data.map(t => t.name));
        }

        if (customRes.ok) setCustomFieldsDef(await customRes.json());

      } catch (error) {
        console.error("Error fetching form data:", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomFieldChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      alert("Please fill in Name and Phone");
      return;
    }

    setLoading(true);
    try {
      const response = await api('/leads', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert("Lead saved successfully!");
        navigate(-1);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error saving lead:", error);
      alert("Failed to connect to backend server");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const bulkData = new FormData();
    bulkData.append('file', file);
    bulkData.append('role', role);

    setLoading(true);
    try {
      const response = await api('/leads/bulk', {
        method: 'POST',
        body: bulkData
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        navigate(-1);
      } else {
        alert("Upload failed. Please check your CSV format.");
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      alert("Error connecting to server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Add New Lead">
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '14px' }}>
          <ChevronLeft size={18} /> Back to Leads
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '24px' }}>
        <div className="card" style={{ background: 'var(--color-background-primary)', padding: '28px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
          <form onSubmit={handleSubmit}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '24px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '12px' }}>
              Contact Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
              <FormGroup label="Full Name *" name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Priya Sharma" />
              <FormGroup label="Phone *" name="phone" value={formData.phone} onChange={handleChange} placeholder="98200 XXXXX" />
              <FormGroup label="Email" name="email" value={formData.email} onChange={handleChange} placeholder="email@example.com" />
              <FormGroup label="Lead Source *" name="source" value={formData.source} onChange={handleChange} type="select" options={leadSources.length > 0 ? leadSources : ['Direct', 'Website', 'Referral']} />
              <FormGroup label="Status" name="status" value={formData.status} onChange={handleChange} type="select" options={leadStatuses.length > 0 ? leadStatuses : ['New', 'Contacted', 'Qualified', 'Follow-up', 'Visit Scheduled', 'Closed', 'Lost']} />
              <FormGroup label="Lead Temperature" name="leadType" value={formData.leadType} onChange={handleChange} type="select" options={leadTypes.length > 0 ? leadTypes : ['Hot', 'Warm', 'Cold']} />
            </div>

            {formData.status === 'Visit Scheduled' && (
              <>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '24px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '12px', color: 'var(--color-brand-primary)' }}>
                  Visit Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                  <FormGroup label="Visit Date" name="visitDate" value={formData.visitDate} onChange={handleChange} type="date" />
                  <FormGroup label="Visit Time" name="visitTime" value={formData.visitTime} onChange={handleChange} type="time" />
                </div>
              </>
            )}

            {customFieldsDef.length > 0 && (
              <>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '24px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '12px' }}>
                  Additional Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                  {customFieldsDef.map(field => (
                    <FormGroup 
                      key={field._id} 
                      label={field.name + (field.required ? ' *' : '')} 
                      name={field.name} 
                      value={formData.customFields[field.name] || ''} 
                      onChange={(e) => handleCustomFieldChange(field.name, e.target.value)} 
                      type={field.type} 
                      options={field.options}
                      required={field.required}
                    />
                  ))}
                </div>
              </>
            )}

            {(role === 'super-admin' || role === 'manager' || role === 'superadmin' || role === 'executive') && (
              <>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '24px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '12px' }}>
                  Assign To (Optional)
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                  <FormGroup label="Assign Telecaller" name="assignedTelecaller" value={formData.assignedTelecaller} onChange={handleChange} type="select" options={employees.filter(u => u.role === 'telecaller').map(u => ({ label: u.name, value: u._id }))} />
                  {(role === 'super-admin' || role === 'manager' || role === 'superadmin') && (
                    <FormGroup label="Assign Executive" name="assignedExecutive" value={formData.assignedExecutive} onChange={handleChange} type="select" options={employees.filter(u => u.role === 'executive' || u.role === 'site-visit-agent').map(u => ({ label: u.name, value: u._id }))} />
                  )}
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
              <button type="submit" disabled={loading} style={{ padding: '10px 24px', borderRadius: 'var(--border-radius-md)', background: loading ? 'var(--color-text-tertiary)' : 'var(--color-brand-primary)', color: 'white', border: 'none', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Saving...' : 'Save Lead'}
              </button>
              <button type="button" onClick={() => navigate(-1)} style={{ padding: '10px 24px', borderRadius: 'var(--border-radius-md)', background: 'white', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div>
          <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>Bulk Upload</h3>
            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
              Upload a CSV file with multiple leads. Auto-distribution will be applied.
            </p>
            <div style={{ border: '2px dashed var(--color-border-secondary)', borderRadius: 'var(--border-radius-lg)', padding: '40px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', background: 'var(--color-background-secondary)', position: 'relative' }}>
              <Upload size={32} color="var(--color-text-tertiary)" />
              <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>{loading ? 'Processing...' : 'Drop your CSV file here'}</div>
              <input type="file" accept=".csv" onChange={handleBulkUpload} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
              <div style={{ fontSize: '12px', color: 'var(--color-brand-primary)', cursor: 'pointer', fontWeight: '500' }}>or browse files</div>
            </div>
            <button onClick={() => window.open('/sample_leads.csv', '_blank')} style={{ marginTop: '16px', width: '100%', padding: '8px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', background: 'white', fontSize: '12px', cursor: 'pointer' }}>
              Download Sample CSV
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

const FormGroup = ({ label, placeholder, name, value, onChange, type = 'text', options = [], required = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>{label}</label>
    {type === 'select' ? (
      <select name={name} value={value} onChange={onChange} required={required} style={{ padding: '10px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', background: 'white', outline: 'none' }}>
        <option value="">None</option>
        {options.map((opt, i) => {
          const val = typeof opt === 'object' ? opt.value : opt;
          const labelText = typeof opt === 'object' ? opt.label : opt;
          return <option key={i} value={val}>{labelText}</option>;
        })}
      </select>
    ) : type === 'textarea' ? (
      <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} required={required} style={{ padding: '10px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', outline: 'none', background: 'white', minHeight: '80px' }} />
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required} style={{ padding: '10px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', outline: 'none', background: 'white' }} />
    )}
  </div>
);

export default AddLead;
